import {
  BatchRun,
  BatchStatus,
  NodeRun,
  NodeRunStatus,
  RecipeRun,
  RecipeRunStatus,
  TERMINAL_WORKFLOW_RUN_STATUSES,
} from '@/types/batch.types';

export const isAnyBatch = (batches: BatchRun[], status: RecipeRunStatus) =>
  batches.some((batch) => isAnyRun(batch, status));

export const isAnyRun = (batch: BatchRun, status: RecipeRunStatus) =>
  batch.recipeRuns.some((run) => run.status === status);

export const getFailedRun = (batch: BatchRun) => batch.recipeRuns.find((run) => run.status === 'FAILED');

const isAnyNodeRunInAnyBatch = (batch: BatchRun, status: NodeRunStatus) =>
  batch.recipeRuns.some((run) => run.nodeRuns.some((nodeRun) => nodeRun.status === status));

export const getBatchStatus = (batch: BatchRun): BatchStatus => {
  // Async batches carry an authoritative parent status, and until the worker fans the batch out there
  // are no child runs to derive one from — an empty `recipeRuns` would otherwise read as COMPLETED.
  if (batch.status && batch.recipeRuns.length === 0) {
    return batch.status;
  }
  if (isAnyRun(batch, 'CANCELED')) {
    return 'CANCELED';
  }
  if (isAnyNodeRunInAnyBatch(batch, 'RUNNING') || isAnyNodeRunInAnyBatch(batch, 'PENDING')) {
    return 'RUNNING';
  }
  if (isAnyRun(batch, 'FAILED')) {
    return 'FAILED';
  }
  return 'COMPLETED';
};

export const isBatchCancellable = (batch: BatchRun): boolean => {
  const status = getBatchStatus(batch);
  // A queued batch has no runs yet, but the server still cancels its parent run.
  return status === 'RUNNING' || status === 'QUEUED' || isAnyRun(batch, 'RUNNING');
};

/**
 * Whether a batch has finished. An async batch is childless until the worker fans it out, so
 * `recipeRuns.every(...)` is vacuously true there and can't be asked on its own.
 */
export const isBatchDone = (batch: BatchRun): boolean =>
  batch.recipeRuns.length > 0
    ? batch.recipeRuns.every(isTerminalRecipeRun)
    : TERMINAL_WORKFLOW_RUN_STATUSES.includes(getBatchStatus(batch));

/** Orders batches newest first. */
export const compareBatchesByRecency = (a: BatchRun, b: BatchRun): number =>
  getBatchRecencyKey(b) - getBatchRecencyKey(a);

const getBatchRecencyKey = (batch: BatchRun): number => {
  const startTime = batch.recipeRuns[0]?.startTime;
  if (startTime) {
    return new Date(startTime).getTime();
  }
  // A childless batch (queued, or canceled before it ever ran) has no run to date it by, so it keeps
  // the arrival time stamped on first merge.
  return batch.firstSeenAt ?? Number.MAX_SAFE_INTEGER;
};

export const getCountOf = (batch: BatchRun, nodeRunStatus: NodeRunStatus, filter?: (nodeId: string) => boolean) =>
  batch.recipeRuns.reduce((total, recipeRun) => {
    const completedNodeRuns = recipeRun.nodeRuns.filter(
      (nodeRun) => nodeRun.status === nodeRunStatus && (filter ? filter(nodeRun.nodeId) : true),
    ).length;
    return total + completedNodeRuns;
  }, 0);

/** Failed model runs with no `refundedAt` timestamp (missing, null, or empty). */
export const countFailedWithNoPlatformRefund = (batch: BatchRun, filter?: (nodeId: string) => boolean): number =>
  batch.recipeRuns.reduce((total, recipeRun) => {
    const n = recipeRun.nodeRuns.filter(
      (nodeRun) => nodeRun.status === 'FAILED' && !nodeRun.refundedAt && (filter ? filter(nodeRun.nodeId) : true),
    ).length;
    return total + n;
  }, 0);

export const getTotalNodeRuns = (batch: BatchRun, filter?: (nodeId: string) => boolean) =>
  batch.recipeRuns.reduce((total, recipeRun) => {
    const nodeRuns = filter ? recipeRun.nodeRuns.filter((nodeRun) => filter(nodeRun.nodeId)) : recipeRun.nodeRuns;
    return total + nodeRuns.length;
  }, 0);

/**
 * The nodes a batch covers. A queued batch has no node runs yet, so it falls back to the ids the
 * server froze at enqueue.
 */
export const getBatchNodeIds = (batch: BatchRun): string[] =>
  batch.recipeRuns.length
    ? [...new Set(batch.recipeRuns.flatMap((run) => run.nodeRuns.map((nodeRun) => nodeRun.nodeId)))]
    : (batch.executionNodeIds ?? []);

export const isNodeInBatch = (nid: string, batch: BatchRun): boolean =>
  // A queued batch has no node runs yet, so its membership comes from the ids the server froze.
  (batch.executionNodeIds?.includes(nid) ?? false) ||
  batch.recipeRuns.some((run) => run.nodeRuns.some((nodeRun) => nodeRun.nodeId === nid));

export const getBatchesForNode = (nid: string, batches: BatchRun[]): BatchRun[] =>
  batches.filter((batch) => isNodeInBatch(nid, batch));

export const isSomeNodesInRunningBatch = (nids: string[], batches: BatchRun[]): boolean => {
  for (const nid of nids) {
    const batchesForNode = getBatchesForNode(nid, batches);
    if (batchesForNode.some((batch) => !isBatchDone(batch))) {
      return true;
    }
  }
  return false;
};

export const getNodeRunsInBatches = (nodeId: string, batches: BatchRun[]): NodeRun[] =>
  batches.flatMap((batch) =>
    batch.recipeRuns.flatMap((run) => run.nodeRuns.filter((nodeRun) => nodeRun.nodeId === nodeId)),
  );

export interface OrderedNodeRun {
  /** 1-based position in the batch, matching the "done / total" count on the run button. */
  runNumber: number;
  /** Store identity, unique per iterator expansion where `nodeRun.id` is not. */
  key: string;
  nodeRun: NodeRun;
}

/**
 * A node's runs across the given batches, ordered the way the user numbers them ("Run 1", "Run 2").
 * An iterator expands one recipe run into several node runs that share a `runIndexInBatch`, so the
 * order is the same `(runIndexInBatch, inputIndex)` pair the results list is built from.
 */
export const getOrderedNodeRunsInBatches = (nodeId: string, batches: BatchRun[]): OrderedNodeRun[] =>
  batches
    .flatMap((batch) =>
      batch.recipeRuns.flatMap((recipeRun) =>
        recipeRun.nodeRuns
          .filter((nodeRun) => nodeRun.nodeId === nodeId)
          .map((nodeRun) => ({
            runIndexInBatch: recipeRun.runIndexInBatch,
            inputIndex: nodeRun.inputIndex ?? recipeRun.inputIndex ?? 0,
            nodeRun,
          })),
      ),
    )
    .sort((a, b) => a.runIndexInBatch - b.runIndexInBatch || a.inputIndex - b.inputIndex)
    .map(({ nodeRun }, index) => ({ runNumber: index + 1, key: getNodeRunMergeId(nodeRun), nodeRun }));

export function areAllRunsFailedOrCompleted(recipeRun: RecipeRun): boolean {
  return recipeRun.nodeRuns.every((run) => run.status === 'FAILED' || run.status === 'COMPLETED');
}

export const getSingleRunProgress = (batch: BatchRun, filter?: (nodeId: string) => boolean): number | null => {
  for (const recipeRun of batch.recipeRuns) {
    for (const nodeRun of recipeRun.nodeRuns) {
      if ((nodeRun.status === 'RUNNING' || nodeRun.status === 'PENDING') && (filter ? filter(nodeRun.nodeId) : true)) {
        return nodeRun.progress ?? 0;
      }
    }
  }
  return null;
};

const preserveBatchUiState = (incomingBatch: BatchRun, previousBatch?: BatchRun): BatchRun => ({
  ...previousBatch,
  ...incomingBatch,
  firstSeenAt: previousBatch?.firstSeenAt ?? Date.now(),
  recipeRuns: mergeRecipeRuns(incomingBatch.recipeRuns, previousBatch?.recipeRuns),
});

const isTerminalRecipeRun = (run: RecipeRun) =>
  run.status === 'COMPLETED' || run.status === 'FAILED' || run.status === 'CANCELED';

const isTerminalNodeRun = (run: NodeRun) =>
  run.status === 'COMPLETED' || run.status === 'FAILED' || run.status === 'CANCELED';

const mergeNodeRun = (incomingRun: NodeRun, previousRun?: NodeRun): NodeRun => {
  if (previousRun && isTerminalNodeRun(previousRun) && !isTerminalNodeRun(incomingRun)) {
    return previousRun;
  }
  if (previousRun && !isTerminalNodeRun(incomingRun)) {
    // Socket pushes and the 10s poll can arrive out of order, so progress only ever moves forward.
    return { ...incomingRun, progress: Math.max(incomingRun.progress ?? 0, previousRun.progress ?? 0) };
  }
  return incomingRun;
};

const getNodeRunMergeId = (run: NodeRun): string => {
  return `${run.nodeId}:${run.runId}${run.inputIndex === undefined ? '' : `:${run.inputIndex}`}`;
};

const mergeNodeRuns = (incomingRuns: NodeRun[], previousRuns: NodeRun[] = []): NodeRun[] => {
  const previousRunsById = new Map(previousRuns.map((run) => [getNodeRunMergeId(run), run]));
  const incomingRunIds = new Set(incomingRuns.map(getNodeRunMergeId));
  const mergedIncomingRuns = incomingRuns.map((run) => mergeNodeRun(run, previousRunsById.get(getNodeRunMergeId(run))));
  const missingTerminalRuns = previousRuns.filter(
    (run) => isTerminalNodeRun(run) && !incomingRunIds.has(getNodeRunMergeId(run)),
  );

  return [...mergedIncomingRuns, ...missingTerminalRuns];
};

const mergeRecipeRun = (incomingRun: RecipeRun, previousRun?: RecipeRun): RecipeRun => {
  if (previousRun && isTerminalRecipeRun(previousRun) && !isTerminalRecipeRun(incomingRun)) {
    return previousRun;
  }
  return {
    ...incomingRun,
    nodeRuns: mergeNodeRuns(incomingRun.nodeRuns, previousRun?.nodeRuns),
  };
};

const mergeRecipeRuns = (incomingRuns: RecipeRun[], previousRuns: RecipeRun[] = []): RecipeRun[] => {
  const previousRunsById = new Map(previousRuns.map((run) => [run.id, run]));
  const incomingRunIds = new Set(incomingRuns.map((run) => run.id));
  const mergedIncomingRuns = incomingRuns.map((run) => mergeRecipeRun(run, previousRunsById.get(run.id)));
  const missingTerminalRuns = previousRuns.filter((run) => isTerminalRecipeRun(run) && !incomingRunIds.has(run.id));

  return [...mergedIncomingRuns, ...missingTerminalRuns];
};

export const mergeBatches = (
  currentBatches: Record<string, BatchRun>,
  incomingBatches: Record<string, BatchRun>,
): Record<string, BatchRun> => ({
  ...currentBatches,
  ...Object.fromEntries(
    Object.entries(incomingBatches).map(([bid, batch]) => [bid, preserveBatchUiState(batch, currentBatches[bid])]),
  ),
});
